/**
 * Compile-time triangle subdivision that exists purely to give TinyGL more lighting
 * sample points.
 *
 * The firmware renderer shades per vertex (Gouraud), so a light only ever contributes
 * where a *vertex* is. A spot cone landing in the middle of a two-triangle floor quad
 * therefore renders almost black: the bright core of the cone is never sampled, because
 * the quad's only vertices are its four far-away corners. Measured on chair22 with the
 * device camera, one subdivision level took the spot from peak 18/92 with zero pixels
 * above 16% brightness to peak 57/92 with 12245 - same light values, same camera.
 *
 * This runs in the Designer rather than the firmware: the geometry cost is paid once at
 * compile time, the device just draws more triangles, and source models do not have to
 * be edited by hand.
 *
 * Positions arriving here already carry the compiler's baked world transforms, so
 * `maxEdgeLength` is a plain world-space distance in model units and stays comparable
 * across primitives and meshes.
 *
 * Strategy: repeatedly bisect the longest edge of any triangle that still has an edge
 * longer than `maxEdgeLength`. Longest-edge bisection keeps the tessellation local and
 * proportional - a large floor gets split, small chair geometry is left alone - instead
 * of uniformly multiplying every triangle in the model.
 *
 * Conformity: the split criterion is edge length alone, and an edge that is too long is
 * too long for both triangles sharing it, so both sides always split it. A per-primitive
 * midpoint cache keyed on the (unordered) source index pair makes them land on the same
 * new vertex, so the result has no T-junctions and no cracks.
 */

// Interpolation rules for the attributes the H3D0 pipeline defines today. Any other
// per-vertex Float32Array on the model is discovered and interpolated linearly, so a
// future attribute is carried through rather than silently dropped.
const KNOWN_VERTEX_ATTRIBUTES = [
  { key: 'positions', components: 3, normalize: false },
  { key: 'normals', components: 3, normalize: true },
  { key: 'uvs', components: 2, normalize: false },
]
const NON_VERTEX_KEYS = new Set(['indices', 'primitives', 'materials', 'lights', 'baseColor', 'report'])
// Backstop against a pathological maxEdgeLength; the report says when it bites.
const DEFAULT_MAX_TRIANGLES = 400000

/**
 * @param {object} model  compiler/optimizer model: positions, normals, indices, primitives, materials
 * @param {{enabled?: boolean, maxEdgeLength?: number, maxTriangles?: number}} options
 * @returns {{model: object, report: object|null}} the same model object when disabled
 */
export function subdivideForLighting(model, options = {}) {
  const opts = normalizeOptions(options)
  // Disabled is a true no-op: the very same object goes on to the writer, so existing
  // H3D0 output stays byte-identical.
  if (!opts.enabled) return { model, report: null }

  if (!Number.isFinite(opts.maxEdgeLength) || opts.maxEdgeLength <= 0) {
    throw new Error('lightingSubdivision.maxEdgeLength must be a finite length greater than zero')
  }
  validateModel(model)

  const attributes = describeAttributes(model)
  const buffers = attributes.map(attribute => Array.from(attribute.source))
  const positions = buffers[0]
  const counter = { next: model.positions.length / 3 }

  const indices = []
  const primitives = []
  let sourceTriangles = 0
  let truncatedTriangles = 0

  for (const primitive of model.primitives) {
    // Per primitive, so a midpoint is never shared across a material boundary.
    const midpoints = new Map()
    const queue = []
    for (let offset = 0; offset < primitive.indexCount; offset += 3) {
      const base = primitive.firstIndex + offset
      queue.push([model.indices[base], model.indices[base + 1], model.indices[base + 2]])
      sourceTriangles += 1
    }

    const firstIndex = indices.length
    for (let cursor = 0; cursor < queue.length; cursor += 1) {
      const triangle = queue[cursor]
      const edge = longestEdge(positions, triangle)
      const overBudget = indices.length / 3 + (queue.length - cursor) > opts.maxTriangles
      if (edge.length <= opts.maxEdgeLength || overBudget) {
        if (edge.length > opts.maxEdgeLength) truncatedTriangles += 1
        indices.push(triangle[0], triangle[1], triangle[2])
        continue
      }

      const midpoint = midpointVertex(attributes, buffers, midpoints, triangle[edge.from], triangle[edge.to], counter)
      const [left, right] = splitTriangle(triangle, edge.index, midpoint)
      queue.push(left, right)
    }

    primitives.push({
      firstIndex,
      indexCount: indices.length - firstIndex,
      materialIndex: primitive.materialIndex,
    })
  }

  const subdivided = { ...model }
  attributes.forEach((attribute, i) => {
    subdivided[attribute.key] = new Float32Array(buffers[i])
  })
  subdivided.indices = new Uint32Array(indices)
  subdivided.primitives = primitives

  return {
    model: subdivided,
    report: {
      maxEdgeLength: opts.maxEdgeLength,
      sourceTriangles,
      subdividedTriangles: indices.length / 3,
      sourceVertices: model.positions.length / 3,
      subdividedVertices: counter.next,
      longestEdgeBefore: longestModelEdge(model.positions, model.indices),
      longestEdgeAfter: longestModelEdge(subdivided.positions, subdivided.indices),
      truncatedTriangles,
      attributes: attributes.map(attribute => attribute.key),
    },
  }
}

/** Bisects `edgeIndex` at `midpoint`, preserving the original winding. */
function splitTriangle([a, b, c], edgeIndex, midpoint) {
  if (edgeIndex === 0) return [[a, midpoint, c], [midpoint, b, c]] // edge a-b
  if (edgeIndex === 1) return [[a, b, midpoint], [a, midpoint, c]] // edge b-c
  return [[a, b, midpoint], [midpoint, b, c]]                      // edge c-a
}

/** Longest of the three edges; ties resolve to the lowest edge index, so it is stable. */
function longestEdge(positions, [a, b, c]) {
  const edges = [
    { index: 0, from: 0, to: 1, length: distance(positions, a, b) },
    { index: 1, from: 1, to: 2, length: distance(positions, b, c) },
    { index: 2, from: 2, to: 0, length: distance(positions, c, a) },
  ]
  let longest = edges[0]
  for (const edge of edges) if (edge.length > longest.length) longest = edge
  return longest
}

function distance(positions, a, b) {
  const dx = positions[a * 3] - positions[b * 3]
  const dy = positions[a * 3 + 1] - positions[b * 3 + 1]
  const dz = positions[a * 3 + 2] - positions[b * 3 + 2]
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/**
 * Midpoint of an edge, created once per (primitive, source edge) so the two triangles
 * sharing that edge stay welded to each other - and only to each other.
 */
function midpointVertex(attributes, buffers, midpoints, a, b, counter) {
  const key = a < b ? `${a}:${b}` : `${b}:${a}`
  const cached = midpoints.get(key)
  if (cached !== undefined) return cached

  attributes.forEach((attribute, i) => {
    const buffer = buffers[i]
    const { components } = attribute
    for (let component = 0; component < components; component += 1) {
      buffer.push((buffer[a * components + component] + buffer[b * components + component]) * 0.5)
    }
    if (attribute.normalize) normalizeTail(buffer, components, a)
  })

  const index = counter.next
  counter.next += 1
  midpoints.set(key, index)
  return index
}

/**
 * Renormalizes the vertex just pushed. Averaging two unit normals shortens them, and a
 * split across a 180-degree normal change cancels them out entirely - that vertex falls
 * back to the first endpoint's normal rather than becoming a zero vector TinyGL would
 * shade as unlit.
 */
function normalizeTail(buffer, components, fallbackVertex) {
  const start = buffer.length - components
  let lengthSquared = 0
  for (let i = 0; i < components; i += 1) lengthSquared += buffer[start + i] * buffer[start + i]
  if (lengthSquared > 1e-12) {
    const inverse = 1 / Math.sqrt(lengthSquared)
    for (let i = 0; i < components; i += 1) buffer[start + i] *= inverse
    return
  }
  for (let i = 0; i < components; i += 1) buffer[start + i] = buffer[fallbackVertex * components + i]
}

/** positions first, so buffers[0] is always the position buffer the splitter measures. */
function describeAttributes(model) {
  const vertexCount = model.positions.length / 3
  const attributes = []
  for (const spec of KNOWN_VERTEX_ATTRIBUTES) {
    const source = model[spec.key]
    if (!source) continue
    if (source.length !== vertexCount * spec.components) {
      throw new Error(`${spec.key} must hold ${spec.components} values per vertex`)
    }
    attributes.push({ ...spec, source })
  }

  for (const [key, value] of Object.entries(model)) {
    if (NON_VERTEX_KEYS.has(key)) continue
    if (attributes.some(attribute => attribute.key === key)) continue
    if (!ArrayBuffer.isView(value) || value instanceof Uint32Array) continue
    if (value.length === 0 || value.length % vertexCount !== 0) continue
    attributes.push({ key, components: value.length / vertexCount, normalize: false, source: value })
  }
  return attributes
}

function longestModelEdge(positions, indices) {
  let longest = 0
  for (let i = 0; i < indices.length; i += 3) {
    const a = indices[i]
    const b = indices[i + 1]
    const c = indices[i + 2]
    longest = Math.max(longest, distance(positions, a, b), distance(positions, b, c), distance(positions, c, a))
  }
  return longest
}

function validateModel(model) {
  if (!model?.positions || !model?.normals || !model?.indices || !Array.isArray(model?.primitives)) {
    throw new Error('lighting subdivision needs a compiled model with positions, normals, indices and primitives')
  }
  const vertexCount = model.positions.length / 3
  for (const primitive of model.primitives) {
    if (primitive.firstIndex + primitive.indexCount > model.indices.length || primitive.indexCount % 3 !== 0) {
      throw new Error('primitive index range is outside the index buffer')
    }
  }
  for (const index of model.indices) {
    if (!Number.isInteger(index) || index < 0 || index >= vertexCount) {
      throw new Error('indices must address the vertex buffer')
    }
  }
}

function normalizeOptions(options) {
  return {
    enabled: Boolean(options?.enabled),
    maxEdgeLength: options?.maxEdgeLength,
    maxTriangles: options?.maxTriangles ?? DEFAULT_MAX_TRIANGLES,
  }
}
